import React, { useState, useEffect, useRef, useCallback } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './SustainableImpactSnapshots.scss';

const SustainableImpactSnapshots = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [modalImageLoaded, setModalImageLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [isVisible, setIsVisible] = useState(false);
  const sliderRef = useRef(null);
  const sectionRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const snapshots = [
    {
      id: 1,
      image: '/images/gallery/afr-img-1.jpg',
      alt: 'African Community with Solar Energy Infrastructure'
    },
    {
      id: 2,
      image: '/images/gallery/afr-img-2.jpg',
      alt: 'Renewable energy project in African village'
    },
    {
      id: 3,
      image: '/images/gallery/afr-img-3.jpg',
      alt: 'Solar panel installation in rural Africa'
    },
    {
      id: 4,
      image: '/images/gallery/afr-img-4.jpg',
      alt: 'Clean energy access for African communities'
    },
    {
      id: 5,
      image: '/images/gallery/afr-img-5.jpg',
      alt: 'Sustainable development project in Africa'
    },
    {
      id: 6,
      image: '/images/gallery/afr-img-6.jpg',
      alt: 'Green energy infrastructure in African region'
    },
    // {
    //   id: 7,
    //   image: '/images/gallery/afr-img-7.jpg',
    //   alt: 'Renewable energy solutions for African communities'
    // },
    {
      id: 8,
      image: '/images/gallery/afr-img-8.jpg',
      alt: 'Solar power implementation in Africa'
    },
    {
      id: 9,
      image: '/images/gallery/afr-img-9.jpg',
      alt: 'Clean energy project benefiting African families'
    },
    {
      id: 10,
      image: '/images/gallery/afr-img-10.jpg',
      alt: 'Sustainable energy access in African villages'
    },
    {
      id: 11,
      image: '/images/gallery/afr-img-11.jpg',
      alt: 'Solar panel deployment in Africa'
    },
    {
      id: 12,
      image: '/images/gallery/afr-img-12.jpg',
      alt: 'Renewable energy infrastructure in African community'
    },
    // {
    //   id: 13,
    //   image: '/images/gallery/afr-img-13.jpg',
    //   alt: 'Clean energy solutions for African development'
    // },
    {
      id: 14,
      image: '/images/gallery/afr-img-14.jpg',
      alt: 'Cleaning cooking and stoves in Africa'
    },
  ];

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    
    // Preload current image immediately with high priority
    const currentImage = snapshots[index].image;
    if (loadedImages.has(currentImage)) {
      // If already loaded, show immediately
      setModalImageLoaded(true);
    } else {
      setModalImageLoaded(false);
      const img = new Image();
      img.fetchpriority = 'high';
      img.onload = () => {
        setLoadedImages((prev) => new Set([...prev, currentImage]));
        setModalImageLoaded(true);
      };
      img.onerror = () => {
        setModalImageLoaded(true); // Show even on error
      };
      img.src = currentImage;
    }

    // Preload adjacent images immediately
    const prevIndex = index === 0 ? snapshots.length - 1 : index - 1;
    const nextIndex = index === snapshots.length - 1 ? 0 : index + 1;
    
    [snapshots[prevIndex].image, snapshots[nextIndex].image].forEach((imageSrc) => {
      if (!loadedImages.has(imageSrc)) {
        const img = new Image();
        img.fetchpriority = 'high';
        img.onload = () => {
          setLoadedImages((prev) => new Set([...prev, imageSrc]));
        };
        img.src = imageSrc;
      }
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImageLoaded(false);
  };

  const nextImage = () => {
    // Use functional update to always get the latest currentImageIndex
    setCurrentImageIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % snapshots.length;
      const nextImageSrc = snapshots[nextIndex].image;
      
      // Check if image is already loaded
      if (loadedImages.has(nextImageSrc)) {
        // If already loaded, show immediately
        setModalImageLoaded(true);
      } else {
        // If not loaded, set loading state and preload
        setModalImageLoaded(false);
        
        const img = new Image();
        img.fetchpriority = 'high';
        img.onload = () => {
          setLoadedImages((prev) => new Set([...prev, nextImageSrc]));
          setModalImageLoaded(true);
        };
        img.onerror = () => {
          setModalImageLoaded(true); // Show even on error
        };
        img.src = nextImageSrc;
      }
      
      return nextIndex;
    });
  };

  const prevImage = () => {
    // Use functional update to always get the latest currentImageIndex
    setCurrentImageIndex((prevIndex) => {
      const prevIdx = (prevIndex - 1 + snapshots.length) % snapshots.length;
      const prevImageSrc = snapshots[prevIdx].image;
      
      // Check if image is already loaded
      if (loadedImages.has(prevImageSrc)) {
        // If already loaded, show immediately
        setModalImageLoaded(true);
      } else {
        // If not loaded, set loading state and preload
        setModalImageLoaded(false);
        
        const img = new Image();
        img.fetchpriority = 'high';
        img.onload = () => {
          setLoadedImages((prev) => new Set([...prev, prevImageSrc]));
          setModalImageLoaded(true);
        };
        img.onerror = () => {
          setModalImageLoaded(true); // Show even on error
        };
        img.src = prevImageSrc;
      }
      
      return prevIdx;
    });
  };

  // Scroll-triggered animations using section ID (same pattern as other sections)
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('sustainable-impact-section');
      if (element) {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if section is 50% visible
        if (rect.top < windowHeight * 0.5 && rect.bottom > windowHeight * 0.5) {
          setIsVisible(true);
        } else if (rect.bottom < -100 || rect.top > windowHeight + 100) {
          // Reset when section is completely out of view
          setIsVisible(false);
        }
      }
    };

    // Check on mount
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track window width for responsive image loading and slider breakpoints
  useEffect(() => {
    // Set initial width immediately
    const updateWidth = () => {
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    };

    // Set immediately
    updateWidth();

    const handleResize = () => {
      updateWidth();
    };

    window.addEventListener('resize', handleResize);
    // Also listen to orientation change for mobile devices
    window.addEventListener('orientationchange', () => {
      setTimeout(updateWidth, 100);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', updateWidth);
    };
  }, []);

  // Preload all images on component mount with proper loading strategy
  useEffect(() => {
    const preloadImages = () => {
      // Preload ALL images immediately for faster modal loading
      const visibleCount = windowWidth <= 480 ? 1 : windowWidth <= 768 ? 2 : 3;
      
      snapshots.forEach((snapshot, index) => {
        // Skip if already loaded
        if (loadedImages.has(snapshot.image)) {
          return;
        }

        const img = new Image();
        // Set fetchpriority for first visible images, but load all
        if (index < visibleCount) {
          img.fetchpriority = 'high';
        } else {
          img.fetchpriority = 'auto';
        }
        
        img.onload = () => {
          setLoadedImages((prev) => new Set([...prev, snapshot.image]));
        };
        
        img.onerror = () => {
          // Still mark as attempted to avoid retries
          setLoadedImages((prev) => new Set([...prev, snapshot.image]));
        };
        
        // Start loading immediately
        img.src = snapshot.image;
      });

      // Mark as preloaded after a short delay (don't wait for all to load)
      setTimeout(() => {
        setImagesPreloaded(true);
      }, 100);
    };

    preloadImages();
  }, [windowWidth]);

  // Force slider to recalculate when windowWidth changes (fix responsive breakpoint issue)
  useEffect(() => {
    if (!sliderRef.current) return;

    const recalculateSlider = () => {
      if (sliderRef.current && sliderRef.current.innerSlider) {
        const innerSlider = sliderRef.current.innerSlider;
        
        // Force responsive breakpoint check
        if (typeof innerSlider.checkResponsive === 'function') {
          innerSlider.checkResponsive(true, true);
        }
        // Update position
        if (typeof innerSlider.setPosition === 'function') {
          innerSlider.setPosition();
        }
        // Force update
        if (typeof innerSlider.update === 'function') {
          innerSlider.update();
        }
      }
    };

    // Recalculate when windowWidth changes with multiple attempts
    const timers = [
      setTimeout(() => recalculateSlider(), 50),
      setTimeout(() => recalculateSlider(), 150),
      setTimeout(() => recalculateSlider(), 300),
    ];

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [windowWidth]); // Recalculate whenever windowWidth changes

  // Preload adjacent images when modal opens and when navigating
  useEffect(() => {
    if (isModalOpen) {
      // Preload current, next, and previous images immediately with high priority
      const imagesToPreload = [
        snapshots[currentImageIndex].image, // Current
        snapshots[currentImageIndex === 0 ? snapshots.length - 1 : currentImageIndex - 1].image, // Previous
        snapshots[currentImageIndex === snapshots.length - 1 ? 0 : currentImageIndex + 1].image, // Next
      ];

      imagesToPreload.forEach((imageSrc) => {
        if (!loadedImages.has(imageSrc)) {
          const img = new Image();
          img.fetchpriority = 'high';
          img.onload = () => {
            setLoadedImages((prev) => new Set([...prev, imageSrc]));
          };
          img.src = imageSrc;
        }
      });

      // Also preload all other images in background
      snapshots.forEach((snapshot) => {
        if (!loadedImages.has(snapshot.image) && !imagesToPreload.includes(snapshot.image)) {
          const img = new Image();
          img.fetchpriority = 'low';
          img.onload = () => {
            setLoadedImages((prev) => new Set([...prev, snapshot.image]));
          };
          img.src = snapshot.image;
        }
      });
    }
  }, [isModalOpen, currentImageIndex]);

  // Reset modal image loaded state when currentImageIndex changes (fallback for direct index changes)
  // This only handles cases where index changes outside of nextImage/prevImage functions
  useEffect(() => {
    if (isModalOpen) {
      const currentImage = snapshots[currentImageIndex].image;
      
      // Check if image is already loaded
      if (loadedImages.has(currentImage)) {
        // If already loaded, show immediately
        setModalImageLoaded(true);
      }
      // Note: nextImage/prevImage handle loading state for navigation
      // This useEffect only ensures already-loaded images show immediately
    }
  }, [currentImageIndex, isModalOpen, loadedImages]);

  // Handle touch events for swipe navigation on mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for a swipe
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swipe left - next image
        nextImage();
      } else {
        // Swipe right - previous image
        prevImage();
      }
    }
    
    // Reset touch positions
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        default:
          break;
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Callback to force responsive recalculation after slider initializes
  const handleSliderInit = useCallback(() => {
    // Force responsive check immediately and with delays
    const forceCheck = () => {
      if (sliderRef.current && sliderRef.current.innerSlider) {
        const innerSlider = sliderRef.current.innerSlider;
        
        // Force responsive breakpoint check
        if (typeof innerSlider.checkResponsive === 'function') {
          innerSlider.checkResponsive(true, true);
        }
        
        // Update position
        if (typeof innerSlider.setPosition === 'function') {
          innerSlider.setPosition();
        }
        
        // Force update
        if (typeof innerSlider.update === 'function') {
          innerSlider.update();
        }
      }
    };
    
    // Immediate check
    forceCheck();
    
    // Delayed checks to ensure it works on all devices
    setTimeout(forceCheck, 50);
    setTimeout(forceCheck, 150);
    setTimeout(forceCheck, 300);
    setTimeout(forceCheck, 500);
  }, []);

  // Determine slider settings based on current window width
  const getSliderSettings = useCallback(() => {
    const baseSettings = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 3000,
      arrows: false,
      initialSlide: 0,
      centerMode: false,
      variableWidth: false,
      adaptiveHeight: true,
      swipeToSlide: true,
      touchMove: true,
      onInit: handleSliderInit,
      onReInit: handleSliderInit,
    };

    // Apply breakpoint-specific settings based on windowWidth
    if (windowWidth <= 480) {
      return {
        ...baseSettings,
        slidesToShow: 1,
        centerMode: false,
        centerPadding: '20px',
      };
    } else if (windowWidth <= 768) {
      return {
        ...baseSettings,
        slidesToShow: 2.5,
      };
    } else if (windowWidth <= 1024) {
      return {
        ...baseSettings,
        slidesToShow: 3.5,
      };
    } else {
      return {
        ...baseSettings,
        slidesToShow: 3.5,
      };
    }
  }, [windowWidth, handleSliderInit]);

  const settings = getSliderSettings();

  return (
    <section id="sustainable-impact-section" ref={sectionRef} className={`sustainable-impact-section ${isVisible ? 'animate' : ''}`}>
      <div className="container-fluid">
        <div className="impact-content">
          {/* Left Side - Title */}
          <div className="impact-left">
            <h2 className="section-title">Snapshots of Sustainable Impact</h2>
          </div>
        </div>
      </div>
      
      {/* Right Side - Slider (Outside container) */}
      <div className="impact-right">
        <div className="slider-container">
          <Slider 
            key={`slider-${windowWidth}`}
            ref={sliderRef} 
            {...settings}
          >
            {snapshots.map((snapshot, index) => {
              const isLoaded = loadedImages.has(snapshot.image);
              const isVisible = index < (windowWidth <= 480 ? 1 : windowWidth <= 768 ? 2 : 3);
              return (
                <div key={snapshot.id} className="slider-slide">
                  <div className="snapshot-item" onClick={() => openModal(index)}>
                    {!isLoaded && (
                      <div className="image-placeholder">
                        <div className="image-spinner"></div>
                      </div>
                    )}
                    <img 
                      src={snapshot.image} 
                      alt={snapshot.alt}
                      className={isLoaded ? 'loaded' : 'loading'}
                      loading={isVisible ? 'eager' : 'lazy'}
                      fetchpriority={isVisible ? 'high' : 'auto'}
                      decoding="async"
                      onLoad={() => setLoadedImages((prev) => new Set([...prev, snapshot.image]))}
                    />
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>

      {/* Gallery Modal */}
      {isModalOpen && (
        <div className="gallery-modal-overlay" onClick={closeModal}>
          <button className="gallery-close-btn" onClick={closeModal} aria-label="Close gallery">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div 
            className="gallery-modal-content" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="gallery-image-container">
              {!modalImageLoaded && (
                <div className="image-placeholder">
                  <div className="image-spinner"></div>
                </div>
              )}
              <img 
                src={snapshots[currentImageIndex].image} 
                alt={snapshots[currentImageIndex].alt}
                className={`gallery-main-image ${modalImageLoaded ? 'loaded' : 'loading'}`}
                loading="eager"
                fetchpriority="high"
                decoding="sync"
                onLoad={() => {
                  setLoadedImages((prev) => new Set([...prev, snapshots[currentImageIndex].image]));
                  setModalImageLoaded(true);
                }}
                onError={() => {
                  setModalImageLoaded(true);
                }}
              />
              <div className="gallery-image-info">
                <p className="gallery-counter">{currentImageIndex + 1} of {snapshots.length}</p>
              </div>
            </div>
            
            {/* Triangular Navigation Buttons */}
            <button className="gallery-nav-btn gallery-prev-btn" onClick={prevImage} aria-label="Previous image">
              <svg width="0" height="0" viewBox="0 0 0 0">
                <polygon points="0,0 20,10 0,20" fill="currentColor"/>
              </svg>
            </button>
            <button className="gallery-nav-btn gallery-next-btn" onClick={nextImage} aria-label="Next image">
              <svg width="0" height="0" viewBox="0 0 0 0">
                <polygon points="0,0 20,10 0,20" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default SustainableImpactSnapshots;




